import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, useMatch, useParams } from 'react-router';

import { ApiError, api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { intlLocale, toLanguage, type Language } from '../i18n.js';
import { useMe } from '../session.js';
import { useSiteFrame } from '../theme.js';
import { SiteRail } from './SiteRail.js';
import { useMediaQuery } from './useMediaQuery.js';
import { StatusBadge } from './ui.js';
import { useSites } from '../sites.js';

export type Grade = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
/** Bytes per MiB: the page weight is shown in MiB on every page, as the rule counts it. */
export const MIB = 1024 * 1024;

export const isGrade = (value: string): value is Grade => /^[A-F]$/.test(value);
export type TileStatus = 'good' | 'warn' | 'bad' | 'unknown' | 'setup';

/** Current UI language as the API expects it. */
export function useLocale(): Language {
  const { i18n } = useTranslation();
  return toLanguage(i18n.language);
}

/** The site's overview (dashboard, side panel, problems): one query, fresh every 30 seconds. */
export function useOverview(siteId: string) {
  const locale = useLocale();
  return useQuery({
    queryKey: ['site', siteId, 'overview', locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/overview', {
          params: { path: { siteId }, query: { locale } },
        }),
      ),
    refetchInterval: 30_000,
  });
}

type SiteOverview = NonNullable<ReturnType<typeof useOverview>['data']>;

/**
 * Why no problem to fix is not "nothing to do" (SP-A2.66): the site is down, or a bot protection
 * or a password keeps our check out; null when it is up and in sight. The outage first, as the
 * dashboard's states show it. The same on the dashboard card and in the side panel.
 */
export function blindSpot(
  o: Pick<SiteOverview, 'openIncident' | 'access'>,
): 'down' | 'bot_protection' | 'auth_required' | null {
  if (o.openIncident?.kind === 'downtime') return 'down';
  return o.access.block;
}

/** All plans with their limits, in the order of the plans (Free first); the same for an hour. */
export function usePlans() {
  return useQuery({
    queryKey: ['plans'],
    queryFn: () => unwrap(api.GET('/billing/plans')),
    staleTime: 60 * 60_000,
  });
}

/**
 * The account's plan with its limits, and whether that is settled: both loaded, or one of them
 * failed (then without a plan).
 */
export function usePlanState() {
  const me = useMe();
  const plans = usePlans();
  const plan = plans.data?.items.find((p) => p.code === me.data?.account.planCode);
  const settled = (plans.isSuccess && me.isSuccess) || plans.isError || me.isError;
  return { plan, settled };
}

/** The account's plan with its limits, once the account and the plans are loaded. */
export function usePlan() {
  return usePlanState().plan;
}

/** Colour of an area's state (sheet: .badge). */
export const TILE_TONE: Record<TileStatus, string> = {
  good: 'ok',
  warn: 'warn',
  bad: 'bad',
  unknown: 'neutral',
  setup: 'acc',
};

/** Where the full details of an area live (the area page links there). */
export const AREA_DETAILS = {
  availability: '/incidents',
  security: '/findings?category=security',
  links: '/links',
  seo: '/findings?category=seo',
  performance: '/speed',
  email: '/findings?category=email',
  compliance: '/findings?category=compliance',
} as const;

/** Site pages (PRD §5: two navigation levels at most). */
const SECTIONS = [
  ['', 'overview'],
  ['/changes', 'changes'],
  ['/findings', 'findings'],
  ['/incidents', 'incidents'],
  ['/links', 'links'],
  ['/speed', 'speed'],
  ['/ai-search', 'ai'],
  ['/modernization', 'modernization'],
  ['/settings', 'settings'],
] as const;

/** The site's address as a link that opens the site itself in a new tab. */
export function SiteAddress({
  url,
  className = '',
  children,
}: {
  url: string;
  className?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <a href={url} target="_blank" rel="noreferrer" className={className}>
      {children}
      <span aria-hidden="true"> ↗</span>
      <span className="sr-only">{` (${t('siteNav.opensNewTab')})`}</span>
    </a>
  );
}

/** Circumference of the small ring (r 40): the grade's share of it is the score. */
const SMALL_RING = 2 * Math.PI * 40;

/** Name, grade and state of the site above the menu (site.css .site-card); from the site list. */
function SiteCard({ siteId }: { siteId: string }) {
  const sites = useSites();
  const site = sites.data?.items.find((s) => s.id === siteId);
  if (!site) return null;
  const grade = site.healthGrade && isGrade(site.healthGrade) ? site.healthGrade : null;
  const score = grade ? (site.healthScore ?? 0) : 0;
  return (
    <div className="site-card">
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <circle cx="50" cy="50" r="40" fill="none" stroke="var(--line-strong)" strokeWidth="12" />
        {grade ? (
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke={`var(--g-${grade.toLowerCase()})`}
            strokeWidth="12"
            strokeDasharray={`${(score / 100) * SMALL_RING} ${SMALL_RING}`}
            transform="rotate(-90 50 50)"
          />
        ) : null}
        <text
          x="50"
          y="62"
          textAnchor="middle"
          fontSize="36"
          fontWeight="800"
          fill="currentColor"
          fontFamily="Manrope, sans-serif"
        >
          {grade ?? '–'}
        </text>
      </svg>
      <div>
        <b>{site.name}</b>
        <SiteAddress url={site.url} className="host">
          {site.host}
        </SiteAddress>
        <StatusBadge state={site.status === 'paused' ? 'paused' : site.uptimeState} />
      </div>
    </div>
  );
}

/** Open problems as a badge in the menu: red when some are urgent. */
function useProblemBadge(siteId: string) {
  const sites = useSites();
  const site = sites.data?.items.find((s) => s.id === siteId);
  if (!site) return null;
  const f = site.openFindings;
  const total = f.critical + f.high + f.medium + f.low + f.info;
  if (total === 0) return null;
  return { total, urgent: f.critical + f.high > 0 };
}

/**
 * The site pages (design/pages/app/site.css, SP-UX10.3): the site and its menu on the left, the page
 * in the middle, the side panel on the right. Narrower, the panel moves above (as in the mock-up);
 * on phones it is one collapsed panel and the menu a strip that scrolls sideways (audit §3). Wide
 * pages (route handle `wide`: Probleme) get the whole width, the panel below them.
 * PRD §5: two navigation levels at most.
 */
export function SiteLayout() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const { wide } = useSiteFrame();
  const problems = useProblemBadge(siteId);
  // Phones get the collapsed panel; one panel in the page, never a hidden second copy.
  const phone = useMediaQuery('(max-width: 56.25rem)');
  const dashboard = useMatch('/sites/:siteId') !== null;
  const help = useMatch('/sites/:siteId/help') !== null;
  const settings = useMatch('/sites/:siteId/settings') !== null;
  return (
    <div className={wide ? 'site-layout wide' : 'site-layout'}>
      <div className="sidebar">
        <Link className="back" to="/">
          <span aria-hidden="true">← </span>
          {t('site.back')}
        </Link>
        <SiteCard siteId={siteId} />
        <nav className="smenu" aria-label={t('siteNav.label')}>
          {SECTIONS.map(([path, key]) => (
            <Fragment key={key}>
              {key === 'settings' ? <hr /> : null}
              <NavLink to={`/sites/${siteId}${path}`} end={path === ''}>
                <span>{t(`siteNav.${key}`)}</span>
                {key === 'findings' && problems ? (
                  <span className={problems.urgent ? 'cnt bad' : 'cnt'}>
                    <span className="sr-only">{t('siteNav.openProblems')}: </span>
                    {problems.total}
                  </span>
                ) : null}
              </NavLink>
            </Fragment>
          ))}
        </nav>
      </div>
      <div className="main">
        {phone ? (
          <SiteRail
            siteId={siteId}
            variant="fold"
            dashboard={dashboard}
            help={help}
            settings={settings}
          />
        ) : null}
        <Outlet />
      </div>
      {phone ? null : (
        <SiteRail
          siteId={siteId}
          variant="side"
          dashboard={dashboard}
          help={help}
          settings={settings}
        />
      )}
    </div>
  );
}

/**
 * The grade as the pill of the sheet (.gpill, owner 11.10, 3a): letter and score on a background
 * tinted by the grade, the text in the main colour; read out as words (never colour alone).
 */
export function GradePill({ grade, score }: { grade: Grade; score: number }) {
  const { t } = useTranslation();
  return (
    <span className={`gpill ${grade.toLowerCase()}`}>
      <span aria-hidden="true">
        {grade} · {score}
      </span>
      <span className="sr-only">{t('health.gradeSr', { grade })}</span>
      <span className="sr-only">{`, ${score} / 100`}</span>
    </span>
  );
}

export function formatPrice(cents: number, language: string) {
  return new Intl.NumberFormat(intlLocale(language), {
    style: 'currency',
    currency: 'EUR',
    currencyDisplay: 'narrowSymbol', // uk-UA would print "49 EUR"; de/en already show €
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/**
 * An amount in euros as typed, in cents (SP-A2.194): the German forms «1.234,50», «1234,50»,
 * «1.234», «79,00», «79» with or without «€»/«EUR», thousands by spaces («1 234,50»), and the
 * English «129.50» and «1,234.50». An empty field is no amount (null). Anything else is
 * undefined rather than a guess – «1,234» is 1,23 € or 1234 € – for the form to refuse.
 */
export function parseEuros(value: string): number | null | undefined {
  if (value.trim() === '') return null;
  let text = value.trim().replace(/^(?:€|eur)\s*|\s*(?:€|eur)$/gi, '');
  if (/\s/.test(text)) {
    if (!/^\d{1,3}(?:\s\d{3})+(?:[.,]\d{1,2})?$/.test(text)) return undefined;
    text = text.replace(/\s/g, '');
  }
  // One or two decimals after the last mark; before it whole euros, maybe in groups of three
  // by the other mark («1.234,50», «1,234.50»), without decimals only by the German dot.
  const [, whole = text, mark, decimals = ''] = /^(.*)([.,])(\d{1,2})$/.exec(text) ?? [];
  const group = mark === '.' ? ',' : '.';
  const grouped = new RegExp(`^\\d{1,3}(?:\\${group}\\d{3})+$`).test(whole);
  if (!/^\d+$/.test(whole) && !grouped) return undefined;
  const cents = Number(whole.replaceAll(group, '')) * 100 + Number(decimals.padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : undefined;
}

/** Invoice amounts: always with cents. */
export function formatMoney(cents: number, language: string) {
  return new Intl.NumberFormat(intlLocale(language), {
    style: 'currency',
    currency: 'EUR',
    currencyDisplay: 'narrowSymbol',
  }).format(cents / 100);
}

/** 32 hex characters; getRandomValues also works outside secure contexts, unlike randomUUID. */
export const randomKey = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
    b.toString(16).padStart(2, '0'),
  ).join('');

type ScanType = 'full' | 'crawl' | 'seo_home' | 'performance';

/** Starts a scan by hand with a fresh Idempotency-Key per click. */
export function useStartScan(siteId: string, type: ScanType) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/sites/{siteId}/scans', {
          params: {
            path: { siteId },
            header: { 'idempotency-key': randomKey() },
          },
          body: { type },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['site', siteId] }),
  });
}

/**
 * The site's system with its version once: the scan stores the name with it ("WordPress 6.6.2"), the
 * profile has the version on its own (SP-A2.6). The profile's version wins: it is the owner's to see.
 */
export const systemLabel = (cms: string, version: string | null | undefined) =>
  version ? `${cms.replace(/\s+\d[\w.-]*$/, '')} ${version}` : cms;

/** A scan the queue closed without scanning: the site was paused or deleted (SP-A2.42). */
export const notScanned = (scan: { status: string; errorCode?: string | null }) =>
  scan.status === 'failed' && (scan.errorCode === 'SITE_PAUSED' || scan.errorCode === 'SITE_GONE');

/** Explains why a scan could not be started. */
export function scanError(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    if (error.code === 'FORBIDDEN') return t('scan.unverified');
    if (error.code === 'RATE_LIMITED') return t('scan.limit');
    // A paused site is not scanned (SP-A2.42).
    if (error.code === 'CONFLICT') return t('scan.paused');
  }
  return errorMessage(error, t as never);
}
